// ---------------------------------------------------------------------------
// useDocumentationV3 — hook que carrega + opera a Documentation V3.
//
// API:
//   GET  api/documentation-v3/topics/
//   POST api/documentation-v3/regen-subtopic/
//   POST api/documentation-v3/accept-regen/
//   POST api/documentation-v3/confirm-current/
//
// Forma de `data` (igual em mock e API):
//   {
//     implementation: { app_name, display_name, default_language },
//     snapshot_available: bool,
//     topics: [
//       { id, slug, title, position, manual_lock, has_bindings,
//         source_blocks, source_helpers,
//         signature_summary, stale_subtopic_count,
//         subtopics: [
//           { id, slug, title, position, content,
//             manual_lock, last_regen_at, last_regen_status, last_regen_actor,
//             drift: { is_stale, added, removed, modified } }
//         ]
//       }
//     ]
//   }
//
// Retorno do hook:
//   { data, loading, error,
//     refresh,            // re-fetch dos topicos
//     regenSubtopic,      // (subtopicId, opts?) -> Promise<candidato>
//     acceptRegen,        // (subtopicId, content, signature, notes?) -> Promise<updatedSub>
//     regenInflight: { [subtopicId]: bool } }
//
// Manipulacao de erros: lanca para o caller via Promise.reject; o
// componente decide se mostra snackbar/modal/etc.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState } from "react";

import { client } from "../../../services/axiosClient";

const TOPICS_URL = "api/documentation-v3/topics/";
const REGEN_URL = "api/documentation-v3/regen-subtopic/";
const ACCEPT_URL = "api/documentation-v3/accept-regen/";
const CONFIRM_URL = "api/documentation-v3/confirm-current/";

export default function useDocumentationV3() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [regenInflight, setRegenInflight] = useState({});

  const fetchTopics = useCallback(async () => {
    try {
      const resp = await client.get(TOPICS_URL);
      setData(resp.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.error_message || e?.message || "fetch_error");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchTopics();
  }, [fetchTopics]);

  const regenSubtopic = useCallback(async (subtopicId, opts = {}) => {
    setRegenInflight((prev) => ({ ...prev, [subtopicId]: true }));
    try {
      const resp = await client.post(REGEN_URL, {
        subtopic_id: subtopicId,
        with_tools: opts.withTools !== false,
        prompt: opts.prompt,
        gabarito_app: opts.gabaritoApp,
        gabarito_topic_slug: opts.gabaritoTopicSlug,
        gabarito_subtopic_slug: opts.gabaritoSubtopicSlug,
        model: opts.model,
        max_tool_rounds: opts.maxToolRounds,
        // Texto livre digitado pelo usuario no modal antes de gerar.
        // Ex.: "faca do zero", "adicione X", "corrija a parte de Y".
        extra_instructions: opts.extraInstructions,
        // HTML "base" da iteracao — corresponde a versao SELECIONADA
        // no carrossel do modal. Quando enviado, o prompt v3 entra em
        // modo iteracao (regra 17): preserva tudo exceto o solicitado.
        // Omitir (ou enviar string vazia) => primeira geracao normal.
        base_html: opts.baseHtml,
      });
      return resp.data;
    } finally {
      setRegenInflight((prev) => {
        const next = { ...prev };
        delete next[subtopicId];
        return next;
      });
    }
  }, []);

  const acceptRegen = useCallback(
    async (subtopicId, content, currentSignature, notes) => {
      const resp = await client.post(ACCEPT_URL, {
        subtopic_id: subtopicId,
        content,
        current_signature: currentSignature,
        notes,
      });
      const updated = resp.data;
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          topics: prev.topics.map((topic) => {
            if (
              topic.id !== updated.subtopic?.id &&
              !topic.subtopics.some((s) => s.id === subtopicId)
            ) {
              return topic;
            }
            const newSubs = topic.subtopics.map((s) =>
              s.id === subtopicId ? updated.subtopic : s,
            );
            const stale = newSubs.filter((s) => s.drift?.is_stale).length;
            return {
              ...topic,
              subtopics: newSubs,
              stale_subtopic_count: stale,
            };
          }),
        };
      });
      return updated;
    },
    [],
  );

  const confirmCurrent = useCallback(async (subtopicId) => {
    const resp = await client.post(CONFIRM_URL, {
      subtopic_id: subtopicId,
    });
    const updated = resp.data;
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        topics: prev.topics.map((topic) => {
          if (!topic.subtopics.some((s) => s.id === subtopicId)) return topic;
          const newSubs = topic.subtopics.map((s) =>
            s.id === subtopicId ? updated.subtopic : s,
          );
          const stale = newSubs.filter((s) => s.drift?.is_stale).length;
          return {
            ...topic,
            subtopics: newSubs,
            stale_subtopic_count: stale,
          };
        }),
      };
    });
    return updated;
  }, []);

  return {
    data,
    loading,
    error,
    refresh: fetchTopics,
    regenSubtopic,
    acceptRegen,
    confirmCurrent,
    regenInflight,
  };
}
